import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from './api';
import { useAuth } from './auth';
import { computeDocsCompletion } from './documents';
import { MISSIONS } from './missions';

export const ROUTE_STEPS = [
  { key: 'sistemas', to: '/app/sistemas' },
  { key: 'rat', to: '/app/rat' },
  { key: 'dpia', to: '/app/dpia' },
  { key: 'documentos', to: '/app/documentos' },
  { key: 'cookies', to: '/app/cookies' },
  { key: 'arco', to: '/app/arco' },
  { key: 'brechas', to: '/app/brechas' },
].map((s) => ({ ...s, title: MISSIONS[s.key].title }));

// Estado de cada paso de la ruta: done (verde), attention (ámbar), pending.
export function useRouteProgress() {
  const { organization } = useAuth();
  const location = useLocation();
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!organization) return;
      try {
        const [sys, acts, triggers, docs, meta, consents, arco, breaches] = await Promise.all([
          api.get('/systems'),
          api.get('/rat/activities'),
          api.get('/dpia/rat-with-triggers'),
          api.get('/documents'),
          api.get('/documents/meta'),
          api.get('/cmp/consents?limit=1'),
          api.get('/arco/requests'),
          api.get('/breaches'),
        ]);
        const docsComp = computeDocsCompletion(
          docs.data, organization?.industria || 'otro', Object.keys(meta.data.types || {}),
        );
        const dpiaPending = triggers.data.filter((r) => !r.dpia_id).length;
        const arcoOpen = arco.data.filter((r) => ['nueva', 'verificada', 'en_proceso'].includes(r.status)).length;
        const arcoOverdue = arco.data.filter((r) => r.status === 'vencida').length;
        const hasPrivacy = docs.data.some((d) => d.doc_type === 'privacy_policy');
        const activeBreaches = breaches.data.filter((b) => !['cerrada', 'descartada'].includes(b.status)).length;
        const status = {
          '/app/sistemas': sys.data.length ? 'done' : 'pending',
          '/app/rat': acts.data.length ? 'done' : 'pending',
          '/app/dpia': !acts.data.length ? 'pending' : (dpiaPending ? 'attention' : 'done'),
          '/app/documentos': !docsComp.requiredMissing.length ? 'done' : (docsComp.totalDone ? 'attention' : 'pending'),
          '/app/cookies': (consents.data.total || 0) > 0 ? 'done' : 'pending',
          '/app/arco': (arcoOpen || arcoOverdue) ? 'attention' : (hasPrivacy ? 'done' : 'pending'),
          '/app/brechas': activeBreaches ? 'attention' : (sys.data.length ? 'done' : 'pending'),
        };
        if (!cancelled) setProgress(status);
      } catch (_e) { /* plan trial/bloqueado: sin indicadores */ }
    })();
    return () => { cancelled = true; };
  }, [organization?.id, location.pathname]);

  const done = progress ? Object.values(progress).filter((s) => s === 'done').length : 0;
  const nextStep = progress
    ? ROUTE_STEPS.find((s) => progress[s.to] !== 'done') || null
    : null;

  return { progress, done, total: ROUTE_STEPS.length, nextStep };
}
